// src/features/tasks/hooks/useUnifiedTaskFiltering.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTasks } from './useTasks';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useAuth } from '../../../context/AuthContext';
import i18n from '../../../i18n';

/**
 * Map UI filter options to server-side filter parameters
 */
const mapFilterToServerParams = (filter, searchTerm, selectedCategory, currentUserId, recurrenceFilter) => {
  const baseParams = {
    limit: 1000, // Get all matching tasks
    offset: 0,
  };

  // Add search term
  if (searchTerm?.trim()) {
    baseParams.search_term = searchTerm.trim();
  }

  // Add category filter
  if (selectedCategory) {
    baseParams.category_ids = [selectedCategory];
  }

  // Add recurrence filter
  console.log("filter", recurrenceFilter)
  if (recurrenceFilter === 'adhock') {
    baseParams.is_recurring = false;
  } else if (recurrenceFilter === 'recurring') {
    baseParams.is_recurring = true;
  }
  // If recurrenceFilter is 'all', don't add is_recurring parameter (null)
      const today = new Date().toISOString().split('T')[0];

  // Add filter-specific parameters
  switch (filter) {
    case 'my-tasks':
      baseParams.assignee_ids = [currentUserId];
      baseParams.states = ['PENDING', 'IN_PROGRESS', 'ON_HOLD'];
      // Only show tasks assigned to current user (primary or secondary)
      break;
    
    case 'pending':
      baseParams.states = ['PENDING'];
      break;
    
    case 'in-progress':
      baseParams.states = ['IN_PROGRESS'];
      break;
    
    case 'completed':
      baseParams.states = ['COMPLETED'];
      break;
    
    case 'overdue':
      // For overdue: tasks with due date before today and active states
      return { 
        ...baseParams, 
        end_date: today,
        date_field: 'due_date',
        states: ['PENDING', 'IN_PROGRESS', 'ON_HOLD']
      };
    
    case 'today':
      // Tasks scheduled for today
      const todayDate = new Date().toISOString().split('T')[0];
      return { 
        ...baseParams, 
        end_date: todayDate,
        date_field: 'scheduled_date'
      };
    
    case 'all':
      return { 
        ...baseParams, 
        has_recurred: false,
      };
    default:
      break;
  }

  return baseParams;
};

/**
 * Unified hook for server-side task filtering using only the filter endpoint
 */
export const useUnifiedTaskFiltering = (initialFilter = 'my-tasks', initialRecurrenceFilter = 'all') => {
  const { showError } = useSnackbar();
  const { user } = useAuth(); // Get current user for assignee filtering
  const {
    filterTasks,
    loading: tasksLoading,
    error: tasksError,
    clearError,
  } = useTasks();

  // Filter state
  const [currentFilter, setCurrentFilter] = useState(initialFilter);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [recurrenceFilter, setRecurrenceFilter] = useState(initialRecurrenceFilter);
  
  // Results
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Debounce search term to avoid excessive API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * Load filtered tasks using server-side filtering
   */
  const loadFilteredTasks = useCallback(async () => {
    if (!user?.staff_member_id) return;

    setFilterLoading(true);
    
    try {
      const filterParams = mapFilterToServerParams(
        currentFilter, 
        debouncedSearchTerm, 
        selectedCategory, 
        user.staff_member_id,
        recurrenceFilter
      );
      
      const result = await filterTasks(filterParams);
      
      if (result.success) {
        setFilteredTasks(result.data?.data?.tasks || []);
      } else {
        showError(result.error || 'Failed to load filtered tasks');
      }
    } catch (error) {
      console.error('Error loading filtered tasks:', error);
      showError(i18n.t('tasks:errors.filter_failed'));
    } finally {
      setFilterLoading(false);
    }
  }, [currentFilter, debouncedSearchTerm, selectedCategory, recurrenceFilter, user?.staff_member_id, filterTasks, showError]);

  /**
   * Load filtered tasks when parameters change
   */
  useEffect(() => {
    if (user?.staff_member_id) {
      loadFilteredTasks();
    }
  }, [loadFilteredTasks, user?.staff_member_id]);

  /**
   * Filter change handler
   */
  const handleFilterChange = useCallback((newFilter) => {
    setCurrentFilter(newFilter);
  }, []);

  /**
   * Category change handler
   */
  const handleCategoryChange = useCallback((event) => {
    const newCategory = typeof event === 'string' ? event : event.target.value;
    setSelectedCategory(newCategory);
  }, []);

  /**
   * Search change handler
   */
  const handleSearchChange = useCallback((event) => {
    const newSearchTerm = typeof event === 'string' ? event : event.target.value;
    setSearchTerm(newSearchTerm);
  }, []);

  /**
   * Recurrence filter change handler
   */
  const handleRecurrenceFilterChange = useCallback((newRecurrence) => {
    setRecurrenceFilter(newRecurrence);
  }, []);

  /**
   * Refresh all data
   */
  const refreshData = useCallback(async () => {
    await loadFilteredTasks();
  }, [loadFilteredTasks]);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setCurrentFilter('all');
    setSelectedCategory('');
    setSearchTerm('');
    setRecurrenceFilter('all');
  }, []);

  /**
   * Check if filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return currentFilter !== 'all' || selectedCategory !== '' || searchTerm !== '' || recurrenceFilter !== 'all';
  }, [currentFilter, selectedCategory, searchTerm, recurrenceFilter]);

  /**
   * Get current filter parameters for debugging
   */
  const getCurrentFilterParams = useCallback(() => {
    if (!user?.staff_member_id) return {};
    return mapFilterToServerParams(currentFilter, debouncedSearchTerm, selectedCategory, user.staff_member_id, recurrenceFilter);
  }, [currentFilter, debouncedSearchTerm, selectedCategory, recurrenceFilter, user?.staff_member_id]);

  return {
    // Data
    filteredTasks,

    // Filter state
    currentFilter,
    selectedCategory,
    searchTerm,
    recurrenceFilter,
    hasActiveFilters,

    // Loading states
    loading: tasksLoading || filterLoading,
    filterLoading,

    // Error handling
    error: tasksError,
    clearError,

    // Actions
    handleFilterChange,
    handleCategoryChange,
    handleSearchChange,
    handleRecurrenceFilterChange,
    refreshData,
    clearFilters,
    getCurrentFilterParams,
  };
};